import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Button } from 'react-bootstrap';
import { TrashFill, PlusLg, PencilFill } from 'react-bootstrap-icons';
import styles from './QueueQuestions.module.scss';
import { useFormik } from 'formik';
import { Booth, GroupType, Question, QuestionInput } from 'app/API';
import * as Yup from 'yup'
import { QuestionAnswerService } from "app/services/QuestionAnswerService";
import { useAppContext } from 'app/context/app-context';

const MAX_ALLOWED_QUESTIONS = 5;

interface IQueueQuestionForm {
    queueQuestion: Array<QuestionModel>
}

interface QuestionModel extends Question {
    isEdit: boolean
}

const queueQuestionFormSchema = Yup.object().shape({
    queueQuestion: Yup.array().required(),
});

const queueQuestionFormInitialValues: IQueueQuestionForm = {
    queueQuestion: [],
}

interface QueueQuestionFormProps {
    booth: Booth | GroupType;
    onQuestionsCountChange?: (count: number) => void;
}

const QueueQuestionFormComponent = forwardRef<{
    submitQueueQuestionForm: () => Promise<void>,
    questionsLength: number
},
    QueueQuestionFormProps
>(({ booth, onQuestionsCountChange }, ref) => {

    const questionAnswerService = new QuestionAnswerService();
    const { jobFair } = useAppContext();

    const prevQuestionModels = useRef<QuestionModel[]>([]);
    const newQuestionRef = useRef<HTMLInputElement | null>(null);
    useImperativeHandle(ref, () => ({
        submitQueueQuestionForm,
        questionsLength: queueQuestionForm.values.queueQuestion.length,
    }));

    const queueQuestionForm = useFormik({
        initialValues: queueQuestionFormInitialValues,
        validationSchema: queueQuestionFormSchema,
        onSubmit: () => {

        }
    });

    const handleQueueQuestionChange = (e: any, index: number) => {
        let _questions = [...queueQuestionForm.values.queueQuestion]
        _questions[index] = {
            id: _questions[index].id,
            name: e.target.value,
            isEdit: true,
            __typename: 'Question',
            rank: index
        };

        queueQuestionForm.setValues({ queueQuestion: _questions }, true);
    }

    const handleQueueQuestionEdit = (index: number, id: string) => {
        queueQuestionForm.values.queueQuestion.forEach(val => { val.isEdit = false });
        if (!queueQuestionForm.values.queueQuestion[index].id)
            queueQuestionForm.values.queueQuestion[index].id = index.toString();
        queueQuestionForm.values.queueQuestion[index].isEdit = true;
        queueQuestionForm.setValues({
            queueQuestion: queueQuestionForm.values.queueQuestion,
        });
    }

    const handleQueueQuestionDelete = (index: number) => {
        queueQuestionForm.values.queueQuestion.splice(index, 1);
        queueQuestionForm.setValues({
            queueQuestion: queueQuestionForm.values.queueQuestion,
        });
    }

    const submitQueueQuestionForm = async () => {

        const questionAnswerService = new QuestionAnswerService();

        if (!jobFair?.id || !booth?.id)
            return;

        const questions: QuestionInput[] = queueQuestionForm.values.queueQuestion.map(x => {
            return {
                id: x.id,
                name: x.name,
                rank: x.rank,
            };
        });

        const convertToModel = (q: (Question | null)[] | null | undefined, isEdit = false): QuestionModel[] => {
            let qm: QuestionModel[] = [];
            if (q && q?.length) {
                q.forEach(x => {
                    if (x)
                        qm.push({
                            id: x?.id,
                            name: x?.name,
                            rank: x?.rank,
                            isEdit,
                            __typename: 'Question'
                        });
                });
            }
            return qm;
        }

        try {
            if (questions.length == 0) {
                await questionAnswerService.deleteAllQuestions(jobFair.id, booth.id);
                prevQuestionModels.current = [];
            }
            const _questions = await questionAnswerService.createOrUpdateAllQuestions(jobFair.id, booth.id, questions);

            if (_questions)
                prevQuestionModels.current = convertToModel(_questions);


        } catch (error) {
            queueQuestionForm.setSubmitting(false);
            queueQuestionForm.setStatus('Something went wrong.');
        }
    }


    const handleQueueQuestionAdd = (e: any) => {

        const newValue: QuestionModel = {
            __typename: 'Question',
            id: (queueQuestionForm.values.queueQuestion?.length || 0).toString(),
            name: newQuestionRef.current?.value || '',
            isEdit: false,
            rank: queueQuestionForm.values.queueQuestion?.length || 0
        };

        if (newQuestionRef.current?.value)
            newQuestionRef.current.value = '';

        queueQuestionForm.setValues({
            queueQuestion: [...queueQuestionForm.values.queueQuestion, newValue]
        }, true);
    }

    useEffect(() => {
        if (!jobFair?.id || !booth?.id) return;

        // setShowQuestionsPreviewModal(false);

        questionAnswerService.getAllQueueQuestionsByBoothId(jobFair.id, booth.id).then((questions: (Question | null)[]) => {
            if (!questions) return;
            const questionModels: QuestionModel[] = [];

            questions.forEach(q => {
                if (!q) return;
                const qm = q as QuestionModel;
                qm.isEdit = false;
                questionModels.push(qm);
            })
            queueQuestionForm.setValues({
                queueQuestion: questionModels,
            });
            prevQuestionModels.current = [...questionModels];
        });

    }, [jobFair?.id, booth?.id]);

    useEffect(() => {
        if (onQuestionsCountChange) {
            onQuestionsCountChange(queueQuestionForm.values.queueQuestion.length);
        }
    }, [queueQuestionForm.values.queueQuestion.length]);


    return (
        <div>
            {queueQuestionForm.status && (
                <div className='mb-lg-15 alert alert-danger'>
                    <div className='alert-text font-weight-bold'>{queueQuestionForm.status}</div>
                </div>
            )}
            {queueQuestionForm.values.queueQuestion && queueQuestionForm.values.queueQuestion.map((item: QuestionModel, index) => {
                return (
                    <div key={`div-${index}`} className='fv-row my-5 d-flex align-items-center'>
                        <input
                            key={`input-${index}`}
                            disabled={!item.isEdit}
                            className='form-control form-control-lg'
                            placeholder='type here to add the question...'
                            value={item?.name || ''}
                            onChange={(e) => handleQueueQuestionChange(e, index)}
                            type="text"
                            name={`${index}`}
                            autoComplete='off'
                        />
                        <Button
                            disabled={item.isEdit}
                            key={`edit-button-${index}`}
                            id={`handle-edit-icons-${index}`}
                            onClick={() => handleQueueQuestionEdit(index, item.id)}
                            variant="none"
                            className={styles.editIcon}
                        >
                            <PencilFill key={`pencil-${index}`} id={`pencil-${index}`} fill="#ffa600" size="20px" />
                        </Button>
                        <Button
                            key={`delete-button-${index}`}
                            id={`handle-delete-icons-${index}`}
                            onClick={() => handleQueueQuestionDelete(index)}
                            variant="none"
                            className={styles.deleteIcon}
                        >
                            <TrashFill key={`trash-${index}`} id={`trash-${index}`} fill="#ff0040" size="20px" />
                        </Button>
                        {queueQuestionForm.touched.queueQuestion && queueQuestionForm.errors.queueQuestion && (
                            <div className='fv-plugins-message-container'>
                                <span role='alert'>{queueQuestionForm.errors.queueQuestion}</span>
                            </div>
                        )}
                    </div>
                )
            })}

            {(queueQuestionForm.values.queueQuestion?.length || 0) < MAX_ALLOWED_QUESTIONS &&
                (<div className='fv-row my-5 d-flex align-items-center'>
                    <input
                        className='form-control form-control-lg'
                        placeholder='type here to add the question...'
                        type="text"
                        name='queueQuestionCreate'
                        autoComplete='off'
                        ref={newQuestionRef}
                        autoFocus />
                    {<Button
                        disabled={((queueQuestionForm.values.queueQuestion?.length || 0 + 1) >= MAX_ALLOWED_QUESTIONS)}
                        id='handle-add-icons'
                        variant="success"
                        className={styles.addIcon}
                        onClick={handleQueueQuestionAdd}
                    >
                        <PlusLg fill="#18924f" size="20px" />
                    </Button>}
                </div>)}
        </div>
    );
});

export default QueueQuestionFormComponent;
